import { useState } from 'react';
import { useInView } from '@/hooks/useInView';
import { Send, CheckCircle, Mail, Instagram } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEB3FORMS_KEY = '4cb19bd1-19bc-44dc-968f-b722bcb66f8a';

export default function KontaktSection() {
  const [ref, inView] = useInView();
  const [form, setForm] = useState({ name: '', firma: '', email: '', vision: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      // 1) Send via Web3Forms (client-side → E-Mail an info@jf-nexus.de)
      let emailSent = false;
      try {
        const w3res = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            access_key: WEB3FORMS_KEY,
            subject: `Neue Kontaktanfrage von ${form.name}`,
            from_name: 'JF-NEXUS Kontaktformular',
            name: form.name,
            Firma: form.firma || '\u2013',
            email: form.email,
            'Vision / Nachricht': form.vision,
            botcheck: '',
          }),
        });
        const w3data = await w3res.json();
        emailSent = w3data.success === true;
      } catch { /* Web3Forms not reachable in preview – ok */ }

      // 2) Always store in MongoDB as backup
      try {
        await fetch(`${API}/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      } catch { /* backend backup failed – still ok if email was sent */ }

      setStatus('success');
      setForm({ name: '', firma: '', email: '', vision: '' });
    } catch {
      setStatus('error');
    }
  };

  const fieldClass = "w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 bg-white/80 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all";

  return (
    <section id="kontakt" ref={ref} data-testid="kontakt-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-600 font-['Outfit']">Kontakt</span>
            <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-slate-900 font-['Outfit']">
              Lassen Sie uns <span className="gradient-text font-normal">sprechen</span>
            </h2>
            <p className="mt-4 text-base text-slate-500 max-w-lg mx-auto">
              Erzählen Sie uns von Ihrer Vision &ndash; wir beraten Sie persönlich und unverbindlich.
            </p>
          </div>

          <div className={`bg-white/70 backdrop-blur-2xl border border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.06)] rounded-3xl p-8 lg:p-10 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>

            {status === 'success' ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={28} className="text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 font-['Outfit']">Nachricht gesendet</h3>
                <p className="mt-2 text-sm text-slate-500">Vielen Dank! Wir melden uns in Kürze bei Ihnen.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-sm text-purple-600 hover:text-purple-700 transition-colors font-medium"
                  data-testid="contact-reset"
                >
                  Weitere Nachricht senden
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Name <span className="text-purple-500">*</span></label>
                    <input type="text" required value={form.name} onChange={handleChange('name')} className={fieldClass} placeholder="Ihr Name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Firma</label>
                    <input type="text" value={form.firma} onChange={handleChange('firma')} className={fieldClass} placeholder="Ihre Firma" data-testid="contact-firma-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">E-Mail <span className="text-purple-500">*</span></label>
                  <input type="email" required value={form.email} onChange={handleChange('email')} className={fieldClass} placeholder="ihre@email.de" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Ihre Vision <span className="text-purple-500">*</span></label>
                  <textarea required value={form.vision} onChange={handleChange('vision')} rows={5} className={`${fieldClass} resize-none`} placeholder="Erzählen Sie uns von Ihrem Projekt – wir machen Ihre Vision digital real." data-testid="contact-vision-input" />
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500" data-testid="contact-error">Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.</p>
                )}

                <button type="submit" disabled={status === 'loading'}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white font-medium text-sm flex items-center justify-center gap-2.5 hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50 active:scale-[0.98]"
                  data-testid="contact-submit-button"
                >
                  {status === 'loading' ? (
                    <span className="animate-pulse">Wird gesendet...</span>
                  ) : (
                    <>
                      <Send size={16} />
                      Nachricht senden
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-slate-400 mt-2 flex items-center justify-center gap-1.5">
                  <Mail size={12} />
                  Ihre Anfrage wird direkt an info@jf-nexus.de gesendet.
                </p>

                <div className="text-center mt-4 pt-4 border-t border-slate-100">
                  <a
                    href="https://instagram.com/jfnexus"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-purple-600 transition-colors"
                    data-testid="contact-instagram-link"
                  >
                    <Instagram size={18} />
                    <span>@jfnexus</span>
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
